/**
 * Keeping a redline anchored to its paragraphs while the document changes.
 *
 * Each redline is tied to the document by named bookmarks. Bookmarks survive
 * ordinary editing, but Word moves, stretches and drops them in ways that
 * break a proposal: an insertion at the edge stretches the bookmark over the
 * neighbouring paragraph, cut and paste deletes it, a rewrite can leave it on
 * a blank paragraph. `AnchorTracker` owns one redline's bookmarks and, on
 * every read, repairs what it can and recovers what is lost through the
 * `RecoveryChain`.
 *
 * Locking: reads take the shared Word lock; the exclusive lock is taken only
 * when a repair or recovery has to write bookmarks.
 *
 * Clocks: attempt timestamps are `Date.now()` epoch milliseconds, so they can
 * be shown and compared with the change ledger; elapsed times are measured
 * with `performance.now()`, which cannot jump.
 */
import { OoxmlRange, type ParagraphIds } from "@common/utils/redline/ooxml-range";
import type { AcceptedTextSpan } from "@common/utils/redline/redline-engine";
import type { CardParagraphAnchor, RedlinePayload } from "@common/utils/redline/wire/redline-payload";
import { ON_PARAGRAPH, type RedlinePlacement } from "@common/utils/redline/wire/redline-placement";
import { RedlineSource, redlineWritesNothingWhereItsParagraphsAreGone } from "@common/utils/redline/redline-source";

import { bulkApplyClockMs, recordBulkApplySpan, timedBulkApplySpan, timedBulkApplySpanSync } from "../bulk-apply-run-timeline";
import { AnchorLog } from "./anchor-log";
import { AnchorMemory } from "./anchor-memory";
import { CandidateScorer } from "./recovery/candidate-scorer";
import { CardAnchorStep } from "./recovery/card-anchor-step";
import { DeletedParagraphStep } from "./recovery/deleted-paragraph-step";
import { ChangeLedgerStep } from "./recovery/change-ledger-step";
import type { DocumentChangeLedger } from "./document-change-ledger";
import type { DocumentEvents } from "./document-events";
import { NeighbourIdStep } from "./recovery/neighbour-id-step";
import { ParagraphIdStep } from "./recovery/paragraph-id-step";
import type { ReanchorClient } from "./reanchor-client";
import { RecoveryBody } from "./recovery/recovery-body";
import { RecoveryChain, type RecoveryAttempt, type RecoveryContext, type RecoveryOutcome, type RecoveryTier, type RecoveryTrigger } from "./recovery/recovery-chain";
import { RecoveryBatches } from "./recovery-batches";
import type { RecoveryConfig } from "./recovery-config";
import { ServerStep } from "./recovery/server-step";
import type { BookmarkedRangeRead, BookmarkEdgeMoves, BookmarkPlacement, WordDocument, WordParagraph, WordSession } from "./word-document";

export interface AnchorDeps {
    word: WordDocument;
    reanchor: ReanchorClient;
    ledger: DocumentChangeLedger;
}

/** The redline of a card that keeps its bookmarks with a tracker. Either shape of card payload. */
export type AttachedRedline = RedlinePayload;

/** A card's redline as the tracker holds it: the payload, and the source read from it once. */
interface AttachedRedlineRead {
    attached: AttachedRedline;
    source: RedlineSource;
}

/**
 * The anchors a card's paragraphs carry, one per paragraph in the card's order,
 * the order `RedlineSource.textsBeforeAuthor` lists the texts in.
 *
 * CASES
 *   - a text card   -> each paragraph's anchor, null where the card carries none
 *   - a markup card -> none: the markup names no paragraph of the document
 */
function cardParagraphAnchorsOf(redline: AttachedRedline): (CardParagraphAnchor | null)[] {
    if (redline.kind !== "texts") return [];
    return redline.paragraphs.map((paragraph) => paragraph.anchor ?? null);
}

/**
 * What a successful read hands the proposal: the range's OOXML, paragraph
 * identities, and the span of each paragraph the redline covers, in accepted
 * text offsets.
 */
export interface AnchoredRead {
    rangeOoxml: string;
    /**
     * Whether `rangeOoxml` is Word's export as given, its paragraphs not paired with Word's
     * (`BookmarkedRangeRead.wordsExportAsGiven`): Apply writes a package built over it as
     * built, since it already ends with Word's paragraph.
     *
     * NULL
     *   Absent, or false, on every read that holds the document's paragraphs alone: a page
     *   cut from the whole-document read, a page read, a package the caller holds.
     */
    wordsExportAsGiven?: boolean;
    paragraphIds: ParagraphIds[];
    uniqueLocalIds: string[];
    coveredSpans: AcceptedTextSpan[];
    /** Bookmark writes made on the way: edge repairs, recoveries and edge moves. */
    bookmarkRepairCount: number;
    elapsedMs: number;
}

/** What the bookmarks still need after a read: edge repairs, or edges moved to specific words. */
interface PendingBookmarkMoves {
    edgesNeedRepair: boolean;
    edgeMoves: BookmarkEdgeMoves;
}

/** The triggers a user's own action gives a reconcile: they keep their trigger on the first loss, so recovery may reach the server. */
const TRIGGERS_A_USER_GAVE: ReadonlySet<RecoveryTrigger> = new Set<RecoveryTrigger>(["apply", "refind"]);

/** The explanation recorded when a recovery found paragraphs Word no longer holds, so no bookmark could be laid on them. */
const PARAGRAPHS_FOUND_ARE_GONE_EXPLANATION = "the paragraphs found are no longer in the document";

/**
 * How many times the body is read for one batch of lost bookmarks: once, and once more when the
 * document's text changed after that read or Word refused the bookmarks laid from it. The second
 * read is the last: what it finds is laid, and what Word still refuses is recorded as not found.
 */
const BODY_READS_PER_BATCH_AT_MOST = 2;

/**
 * One tracker's ask to reconcile, queued for the next hold of the write lock: the tracker, the
 * trigger it reconciles under, and whether the asker wants the tracker's range read back from Word.
 *
 * WHY `readsRangeBack` is a choice
 *   Apply All asks only whether each card's bookmarks are on the document again, and reads the whole
 *   document once after every recovery. The read of one tracker's range after its bookmarks are laid
 *   back is a Word round trip of about 350 ms the run throws away: 26 of them in a 13-card run.
 */
interface ReconcileRequest {
    tracker: AnchorTracker;
    trigger: RecoveryTrigger;
    readsRangeBack: boolean;
}

/** What a reconcile that was asked not to read the range back reports for a tracker whose bookmarks it laid back: found, with no range read. */
export interface BookmarksLaidBack {
    readonly laidBack: true;
}

const BOOKMARKS_LAID_BACK: BookmarksLaidBack = { laidBack: true };

/** What one queued reconcile settles with: the range read back, the bookmarks laid back with none read, or null when the bookmarks could not be found. */
type ReconcileResult = AnchoredRead | BookmarksLaidBack | null;

/** One lost tracker to recover, and the trigger its recovery runs under. */
interface LostRecoveryAttempt {
    tracker: AnchorTracker;
    trigger: RecoveryTrigger;
}

/** What recovering one lost tracker's bookmarks came to: whether they stand on the document again. */
interface LostRecoveryOutcome {
    bookmarksLaid: boolean;
}

/** The outcome of a lost tracker that no recovery was due for, or whose bookmarks no recovery laid. */
const NO_BOOKMARKS_LAID: LostRecoveryOutcome = { bookmarksLaid: false };

export class AnchorTracker {
    private readonly memory: AnchorMemory;
    private readonly chain: RecoveryChain;
    /** The chain's first step, kept so a recovery from a body read already taken applies the step's own rule (`paragraphsFoundIn`). */
    private readonly paragraphIdStep: ParagraphIdStep;
    /** The first card's redline attached: the paragraphs the memory remembers the bookmarks by. */
    private redline: AttachedRedlineRead | null = null;
    /**
     * The first card on these bookmarks whose redline writes words back where its paragraphs
     * are gone (`redlineWritesNothingWhereItsParagraphsAreGone` false), whichever card attached
     * first. Recovery places the bookmarks for it when the paragraphs are gone.
     *
     * WHY not the first card attached
     *   Every card on the same bookmarks shares this tracker. An Issue's comment and its remedy
     *   sit on one paragraph, and the live-review widget follows the comment before Apply All
     *   reaches the remedy, so the comment's redline, which writes nothing, was the first
     *   attached. Recovery then took the remedy for a card that writes nothing, never placed it
     *   after the paragraph above, and failed it with "We cannot find where to apply this"
     *   (-26's live run on 36e996623, 2026-10-18: "Insurance amount" on a hand-deleted 11.1).
     *
     * NULL
     *   No card attached so far writes anything where its paragraphs are gone: a comment, a
     *   reply, a card that only deletes them, or none attached.
     */
    private redlineWrittenWhereItsParagraphsAreGone: AttachedRedlineRead | null = null;
    private placementValue: RedlinePlacement = ON_PARAGRAPH;

    constructor(
        readonly bookmarkNames: string[],
        private readonly deps: AnchorDeps,
        config: RecoveryConfig,
    ) {
        this.memory = new AnchorMemory(config);
        const scorer = new CandidateScorer(config);
        this.paragraphIdStep = new ParagraphIdStep(scorer);
        this.chain = new RecoveryChain([
            this.paragraphIdStep,
            new NeighbourIdStep(scorer),
            new ChangeLedgerStep(scorer, deps.ledger),
            new CardAnchorStep(),
            new DeletedParagraphStep(),
            new ServerStep(deps.reanchor, deps.ledger, config),
        ]);
    }

    /**
     * A card on these bookmarks attaches its redline.
     *
     * CASES
     *   - the first redline attached                      -> the memory keeps the paragraphs it was
     *                                                        drafted against, so a recovery that has
     *                                                        nothing read to go on still knows what
     *                                                        to look for
     *   - the first one that writes words back where its
     *     paragraphs are gone                             -> kept as the card recovery places the
     *                                                        bookmarks for (`redlineWrittenWhereItsParagraphsAreGone`)
     *   - any later one                                    -> nothing more is kept
     *
     * EXAMPLE
     *   comment on 11.1 attached, then the remedy rewriting 11.1
     *   -> the memory keeps 11.1 as the comment read it; the remedy is the card that writes 11.1 back
     */
    attachRedline(redline: AttachedRedline): void {
        /** WHEN a card that writes its paragraphs back is already attached CONSEQUENCE there is nothing more to keep. */
        if (this.redlineWrittenWhereItsParagraphsAreGone !== null) return;
        /** WHEN the same payload attaches again (a card rendered again) CONSEQUENCE it was judged when it first attached. */
        if (this.redline !== null && this.redline.attached === redline) return;
        const redlineRead: AttachedRedlineRead = { attached: redline, source: RedlineSource.fromPayload(redline) };
        /** WHEN no card attached before this one CONSEQUENCE the memory remembers the bookmarks by its paragraphs. */
        if (this.redline === null) {
            this.redline = redlineRead;
            this.memory.rememberRedlineTexts(redlineRead.source.textsBeforeAuthor, cardParagraphAnchorsOf(redline));
        }
        /** WHEN this card writes words back where its paragraphs are gone CONSEQUENCE recovery places the bookmarks for it. */
        if (!redlineWritesNothingWhereItsParagraphsAreGone(redline)) this.redlineWrittenWhereItsParagraphsAreGone = redlineRead;
    }

    /** Where the last recovery placed the redline; `on_paragraph` until the server says otherwise, and again after Apply settles it. */
    get placement(): RedlinePlacement {
        return this.placementValue;
    }

    /**
     * Whether recovery may ask the server for these bookmarks once the local steps found
     * nothing: false when a card is attached and every card on them writes nothing back where
     * its paragraphs are gone (it only deletes them, or it is a comment's or a reply's
     * paragraph, its text left as it is); true otherwise, no card attached included.
     *
     * WHY
     *   A deletion placed by a guess deletes someone else's words, as step 4 of the #15 live
     *   run deleted the paragraph that quoted 7.2 (2026-10-18). Those cards recover by their
     *   local steps alone (`reconcileIn`), and Apply All never leaves them for its server pass.
     *
     * EXAMPLE
     *   an Issue's comment alone on 11.1                     -> false
     *   the same comment and its remedy rewriting 11.1       -> true
     *   a markup remedy, no text card attached               -> true
     */
    get serverMayPlaceItsCard(): boolean {
        return this.redline === null || this.redlineWrittenWhereItsParagraphsAreGone !== null;
    }

    lastAttempt(): RecoveryAttempt | null {
        return this.memory.lastAttempt();
    }

    /** After Apply has written the redline as new paragraphs and bookmarked them, the redline sits on its own paragraphs again. */
    settleOnParagraph(): void {
        this.placementValue = ON_PARAGRAPH;
    }

    /**
     * The paragraphs this tracker's remembered ids still name in a list of the body's
     * paragraphs, when they still read like the text the redline last covered: the recovery
     * chain's first step (`ParagraphIdStep`), answered from a list the caller already holds
     * (`WordSession.listedParagraphs`). Asks Word nothing and writes nothing.
     *
     * WHY
     *   Recovering the bookmarks of twenty comments one at a time finds each paragraph in Word
     *   with reads of its own. When the paragraph-id step answers, the one list of the body's
     *   paragraphs answers it for all twenty, and the caller lays all the bookmarks in one write
     *   (`DocumentBookmarks.layOverTogether`).
     *
     * CASES
     *   - the ids name paragraphs the list holds, and they read like the last covered text -> those paragraphs
     *   - none is in the list, or they read like something else                              -> null: the
     *     chain's later steps (`reconcile`) decide
     *
     * RETURNS
     *   the paragraphs to lay the bookmarks over, texts as the list gives them.
     *
     * EXAMPLE
     *   comment remembered on p-92 "9.2 The receiving party shall protect …", bookmark deleted,
     *   the list still holds p-92 with that text -> [p-92]
     */
    paragraphsFoundIn(listedParagraphsById: ReadonlyMap<string, WordParagraph>): WordParagraph[] | null {
        const paragraphsNamed = this.memory.lastUniqueLocalIds.flatMap((uniqueLocalId) => listedParagraphsById.get(uniqueLocalId) ?? []);
        const result = this.paragraphIdStep.recoveryFrom(paragraphsNamed, this.memory);
        return result.outcome === "recovered" ? result.paragraphs : null;
    }

    /**
     * Records that a caller laid this tracker's bookmarks over the paragraphs `paragraphsFoundIn`
     * named: the placement is on those paragraphs, and the attempt is on the tracker's history as
     * the paragraph-id step's recovery, as a reconcile records its own.
     */
    settleOnParagraphsLaidOver(): void {
        this.placementValue = ON_PARAGRAPH;
        const attempt: RecoveryAttempt = {
            attemptedAtMs: Date.now(),
            trigger: "apply",
            tier: this.paragraphIdStep.tier,
            outcome: "recovered",
            explanation: null,
            placement: this.placementValue,
            candidates: [],
            checks: null,
        };
        this.memory.record(attempt);
        AnchorLog.record({ kind: "recovery", bookmarkNames: this.bookmarkNames, attempt });
    }

    /** Cheap path first: a read under the shared lock. Only a lost bookmark or a needed repair escalates to the write lock. */
    async reconcile(trigger: RecoveryTrigger): Promise<AnchoredRead | null> {
        const reconciled = await this.reconcileOrFind(trigger, true);
        /**
         * WHEN the reconcile was asked to read the range back
         *   CONSEQUENCE
         *     "laid back" is never its answer (it is given only to a reconcile asked not to read),
         *     so what is not null is the range.
         */
        return reconciled === null || "laidBack" in reconciled ? null : reconciled;
    }

    /**
     * Whether this tracker's bookmarks are on the document, laid back where recovery found them
     * when they were lost, with no read of the tracker's range back from Word.
     *
     * WHY
     *   Apply All asks only whether each card was found, and reads the whole document once after
     *   every recovery. Reading every recovered tracker's range back was two round trips to Word
     *   a tracker, 350 ms each, and 26 of them in a 13-card run; the run used none of what they read.
     *
     * RETURNS
     *   true when the bookmarks stand on the document now, false when recovery found nothing.
     *
     * EXAMPLE
     *   nine cards whose bookmarks were deleted -> nine trackers ask together -> one body read, one
     *   write laying nine bookmarks, nine `true`, and no range read
     */
    async reconcileToFind(trigger: RecoveryTrigger): Promise<boolean> {
        return (await this.reconcileOrFind(trigger, false)) !== null;
    }

    private async reconcileOrFind(trigger: RecoveryTrigger, readsRangeBack: boolean): Promise<ReconcileResult> {
        const { word, ledger } = this.deps;
        ledger.ensureSubscribed();
        /**
         * WHEN the tracker is not primed
         *   CONSEQUENCE
         *     Use the write-lock path first.
         *     - no remembered paragraphs yet -> shared-lock read is not trustworthy
         *     - bookmarks may need repair    -> repair can only happen under write lock
         */
        if (!this.memory.primed) return this.reconcileInTheNextBatch(trigger, readsRangeBack);
        const startedAtMs = performance.now();
        const read = await word.withRead((session) => session.bookmarks.readRange(this.bookmarkNames));
        /**
         * WHEN the shared-lock read finds no bookmark range
         *   CONSEQUENCE
         *     Escalate to the write-lock path.
         *     - Word may have dropped the bookmarks
         *     - recovery may now be due, and recovery writes
         */
        if (!read) {
            /**
             * WHEN the bookmarks are already marked lost and no retry is due for this trigger
             *   CONSEQUENCE
             *     Return null instead of spending another recovery attempt yet.
             *
             * NULL
             *   Bookmarks are still lost, and backoff says not to retry now.
             *   Caller keeps the last good anchored read.
             */
            if (this.memory.isLost && !this.memory.recoveryDue(trigger)) return null;
            return this.reconcileInTheNextBatch(trigger, readsRangeBack);
        }
        const pending = this.pendingMoves(read);
        const edgeRepairIsNotNeeded = !pending.edgesNeedRepair;
        const startEdgeMoveIsNotNeeded = edgeRepairIsNotNeeded ? !pending.edgeMoves.startAfterWord : false;
        const endEdgeMoveIsNotNeeded = startEdgeMoveIsNotNeeded ? !pending.edgeMoves.endBeforeWord : false;
        const bookmarkWriteIsNotNeeded = edgeRepairIsNotNeeded && startEdgeMoveIsNotNeeded && endEdgeMoveIsNotNeeded;
        /**
         * WHEN no bookmark write is owed after the read
         *   CONSEQUENCE
         *     Stay on the read-only path and finish from this read.
         *     - no edge repair due
         *     - no start move due
         *     - no end move due
         */
        if (bookmarkWriteIsNotNeeded) {
            return this.finish({ read, bookmarkRepairCount: 0, startedAtMs });
        }
        return this.reconcileInTheNextBatch(trigger, readsRangeBack);
    }

    /**
     * Reconciles under the write lock, in the same hold as every other tracker that asks for the
     * lock before it is granted (`RecoveryBatches`), so the trackers whose bookmarks are lost are
     * recovered from one read of the body and laid in one write (`reconcileBatch`).
     *
     * WHY
     *   Twenty trackers whose bookmarks were lost together each took the lock in turn, each read
     *   the body and each asked Word for paragraphs one by one: the same document read twenty
     *   times, one after another, and every paragraph it holds asked for again.
     *
     * EXAMPLE
     *   a document reopened with every paragraph bookmark re-minted, twenty cards on screen
     *   -> twenty trackers reconcile "loss" in the same turn -> one lock hold, one body read, one write
     */
    private reconcileInTheNextBatch(trigger: RecoveryTrigger, readsRangeBack: boolean): Promise<ReconcileResult> {
        return AnchorTracker.recoveryBatchesOf(this.deps.word).recoverTogether({ tracker: this, trigger, readsRangeBack });
    }

    /** One batching queue per document, shared by every tracker of it, so trackers built with a plain `{ word, reanchor, ledger }` still batch together. */
    private static readonly recoveryBatchesByWordDocument = new WeakMap<WordDocument, RecoveryBatches<ReconcileRequest, ReconcileResult>>();

    private static recoveryBatchesOf(word: WordDocument): RecoveryBatches<ReconcileRequest, ReconcileResult> {
        const existingRecoveryBatches = AnchorTracker.recoveryBatchesByWordDocument.get(word);
        /** CASES: the document has a queue -> it; none yet -> this document's first, kept for every tracker that follows. */
        if (existingRecoveryBatches !== undefined) return existingRecoveryBatches;
        const newRecoveryBatches = new RecoveryBatches<ReconcileRequest, ReconcileResult>(word, (session, requests) => AnchorTracker.reconcileBatch(session, word.events, requests));
        AnchorTracker.recoveryBatchesByWordDocument.set(word, newRecoveryBatches);
        return newRecoveryBatches;
    }

    /**
     * Reconciles every tracker that asked for the one hold of the write lock.
     *
     * The trackers whose bookmarks Word holds nowhere are told apart in one question to Word
     * (`DocumentBookmarks.bookmarkNamesNotFoundInDocument`) and recovered together (`recoverLostTogether`): the body is read
     * once, every lost tracker is resolved against that read, and every bookmark found is laid in
     * one write. Each tracker then finishes its own reconcile (`reconcileIn`).
     *
     * CASES, per request
     *   - the tracker's bookmarks are all absent, and the shared recovery ran -> it finishes over what the recovery laid
     *   - they are all absent, and the shared recovery failed                  -> the request fails with that failure
     *   - some bookmark is present                                             -> the tracker reconciles on its own
     *
     * WHY a failed question about the bookmarks does not fail the batch
     *   It only sorts the trackers: when Word will not answer it, each tracker finds its own bookmarks
     *   absent or present when it reads them, and fails or succeeds there.
     *
     * RETURNS
     *   one outcome per request, in the request's order.
     */
    private static async reconcileBatch(session: WordSession, events: DocumentEvents, requests: ReconcileRequest[]): Promise<PromiseSettledResult<ReconcileResult>[]> {
        const bookmarkNamesAsked = Array.from(new Set(requests.flatMap(({ tracker }) => tracker.bookmarkNames)));
        let bookmarkNamesAbsent = new Set<string>();
        if (bookmarkNamesAsked.length > 0) {
            try {
                bookmarkNamesAbsent = await timedBulkApplySpan("recovery_absent_bookmarks_asked", () => session.bookmarks.bookmarkNamesNotFoundInDocument(bookmarkNamesAsked), { bookmarks: bookmarkNamesAsked.length });
            } catch (error) {
                AnchorLog.record({ kind: "error", bookmarkNames: bookmarkNamesAsked, step: "tell lost bookmarks apart", message: AnchorLog.messageOf(error) });
            }
        }
        const trackersLost = new Set(requests.map(({ tracker }) => tracker).filter((tracker) => tracker.bookmarkNames.length > 0 && tracker.bookmarkNames.every((bookmarkName) => bookmarkNamesAbsent.has(bookmarkName))));
        /** The trigger each lost tracker recovers under; null where no recovery is due for it yet. */
        const lossTriggerByLostTracker = new Map<AnchorTracker, RecoveryTrigger | null>();
        for (const { tracker, trigger } of requests) {
            if (trackersLost.has(tracker) && !lossTriggerByLostTracker.has(tracker)) lossTriggerByLostTracker.set(tracker, tracker.lossRecoveryTrigger(trigger));
        }
        const recoveryAttempts = Array.from(lossTriggerByLostTracker.entries()).flatMap(([tracker, lossTrigger]) => (lossTrigger === null ? [] : [{ tracker, trigger: lossTrigger }]));
        const lostRecoveryByTracker = new Map<AnchorTracker, LostRecoveryOutcome>();
        let lostRecoveryFailure: { error: unknown } | null = null;
        try {
            const lostRecoveryOutcomes = await AnchorTracker.recoverLostTogether(session, events, recoveryAttempts);
            recoveryAttempts.forEach(({ tracker }, attemptIndex) => lostRecoveryByTracker.set(tracker, lostRecoveryOutcomes[attemptIndex]));
        } catch (error) {
            lostRecoveryFailure = { error };
        }
        const outcomes: PromiseSettledResult<ReconcileResult>[] = [];
        const trackersFinishedStartedAtMs = bulkApplyClockMs();
        for (const { tracker, trigger, readsRangeBack } of requests) {
            if (!trackersLost.has(tracker)) {
                outcomes.push(await AnchorTracker.settled(() => tracker.reconcileIn(session, trigger)));
            } else if (lostRecoveryFailure !== null) {
                outcomes.push({ status: "rejected", reason: lostRecoveryFailure.error });
            } else {
                const lostRecovery = lostRecoveryByTracker.get(tracker) ?? NO_BOOKMARKS_LAID;
                /**
                 * CASES, for a lost tracker the shared recovery handled
                 *   - the asker wants the range read back        -> the tracker finishes over what the recovery laid (`reconcileIn`)
                 *   - the asker only wants to know it was found  -> found when its bookmarks were laid; no read of its range
                 */
                if (readsRangeBack) outcomes.push(await AnchorTracker.settled(() => tracker.reconcileIn(session, trigger, lostRecovery)));
                else outcomes.push({ status: "fulfilled", value: lostRecovery.bookmarksLaid ? BOOKMARKS_LAID_BACK : null });
            }
        }
        recordBulkApplySpan("recovery_trackers_finished", trackersFinishedStartedAtMs, { trackers: requests.length, lost: trackersLost.size });
        return outcomes;
    }

    /** What one reconcile came to, as a settled outcome: its answer, or the failure that ended it. */
    private static async settled<Reconciled>(reconcile: () => Promise<Reconciled>): Promise<PromiseSettledResult<Reconciled>> {
        try {
            return { status: "fulfilled", value: await reconcile() };
        } catch (reason) {
            return { status: "rejected", reason };
        }
    }

    /**
     * Recovers every lost tracker from one read of the body and lays every bookmark found in one
     * write: no question to Word about a single paragraph, and no write per tracker.
     *
     * The body is read once and every tracker's chain runs over that read at the same time (a step
     * that asks the server waits on the server alone). The chains' findings are laid together
     * (`DocumentBookmarks.layOverTogether`), and only then is each tracker's attempt recorded.
     *
     * CASES
     *   - no attempt                                              -> nothing is read, nothing is written
     *   - the text did not change after the read, the lay is taken -> every outcome stands as found
     *   - the text changed after the read began                    -> the body is read again once, and
     *                                                                 every tracker is resolved against that read
     *   - Word refused the bookmarks laid from the read            -> the same: read again once, resolve again,
     *                                                                 lay again; laying a name again moves it, so
     *                                                                 a bookmark the first write placed stays put
     *   - the second read's findings are changed or refused too    -> they are laid or recorded as not found as they stand
     *
     * WHY the read is taken again and not each paragraph asked for
     *   A paragraph the user deleted or retyped while the chains ran is a change to the document:
     *   the document is read as a whole again and every tracker is resolved against it, the same
     *   way as against the first read.
     *
     * EXAMPLE
     *   twenty lost comment bookmarks -> one body read, twenty chains, one write of twenty placements
     *
     * RETURNS
     *   one outcome per attempt, in the attempt's order.
     */
    private static async recoverLostTogether(session: WordSession, events: DocumentEvents, attempts: LostRecoveryAttempt[], bodyReadsTaken = 0): Promise<LostRecoveryOutcome[]> {
        /** WHEN no tracker is to be recovered CONSEQUENCE the body is not read: nothing would use it. */
        if (attempts.length === 0) return [];
        const bodyRead = await timedBulkApplySpan("recovery_body_read", () => session.readBodyForRecovery(), { read: bodyReadsTaken + 1 });
        const mayReadTheBodyAgain = bodyReadsTaken + 1 < BODY_READS_PER_BATCH_AT_MOST;
        const body = new RecoveryBody(bodyRead);
        const findings = await timedBulkApplySpan("recovery_chains_run", () => Promise.all(attempts.map(({ tracker, trigger }) => tracker.findPlacement(body, trigger))), { trackers: attempts.length });
        /** WHEN the text changed after the read began CONSEQUENCE what the chains found may be a paragraph that is no longer there: read again and resolve again. */
        if (events.contentChangedSince(bodyRead.coversEventsThroughSequence) && mayReadTheBodyAgain) return AnchorTracker.recoverLostTogether(session, events, attempts, bodyReadsTaken + 1);
        const placements: BookmarkPlacement[] = [];
        attempts.forEach(({ tracker }, attemptIndex) => {
            const finding = findings[attemptIndex];
            if (finding.result.outcome === "recovered") placements.push({ bookmarkNames: tracker.bookmarkNames, paragraphs: finding.result.paragraphs });
        });
        let bookmarksWereLaid = true;
        if (placements.length > 0) {
            try {
                await timedBulkApplySpan("recovery_bookmarks_laid", () => session.bookmarks.layOverTogether(placements), { placements: placements.length });
            } catch (error) {
                AnchorLog.record({ kind: "error", bookmarkNames: placements.flatMap(({ bookmarkNames }) => bookmarkNames), step: "lay recovered bookmarks together", message: AnchorLog.messageOf(error) });
                /** WHEN Word refused the write and the body may be read again CONSEQUENCE what the refusal says about the paragraphs is read from the document afresh. */
                if (mayReadTheBodyAgain) return AnchorTracker.recoverLostTogether(session, events, attempts, bodyReadsTaken + 1);
                bookmarksWereLaid = false;
            }
        }
        return attempts.map(({ tracker, trigger }, attemptIndex) => {
            const finding = findings[attemptIndex];
            const bookmarksLaid = finding.result.outcome === "recovered" && bookmarksWereLaid;
            tracker.recordRecovery(trigger, finding, bookmarksLaid);
            return { bookmarksLaid };
        });
    }

    /** For a caller that already holds the write lock on a Word context. */
    reconcileWithin(context: Word.RequestContext, trigger: RecoveryTrigger): Promise<AnchoredRead | null> {
        return this.reconcileIn(this.deps.word.sessionFor(context), trigger);
    }

    /**
     * User-triggered: forget and recover, allowed to reach the server, and remember
     * what the recovery laid the bookmarks on.
     *
     * WHY the memory is primed from a read of the relocated paragraphs
     *   `forget` drops the ids, paraIds and neighbours the tracker knew, and a recovery
     *   remembers nothing on its own, so the next loss had nothing to go on but the
     *   server (T20, 2026-10-16). Read once the bookmarks are laid, the relocated
     *   paragraphs are what a later loss recovers by id and neighbour before the
     *   server is asked.
     */
    async refind(): Promise<RecoveryAttempt | null> {
        /** Drops all memory and the bookmarks themselves. */
        const forget = async (session: WordSession): Promise<void> => {
            this.memory.forget();
            this.placementValue = ON_PARAGRAPH;
            await session.bookmarks.delete(this.bookmarkNames);
            AnchorLog.record({ kind: "forget", bookmarkNames: this.bookmarkNames });
        };
        this.deps.ledger.ensureSubscribed();
        return this.deps.word.withWrite(async (session) => {
            await forget(session);
            const startedAtMs = performance.now();
            const [{ bookmarksLaid }] = await AnchorTracker.recoverLostTogether(session, this.deps.word.events, [{ tracker: this, trigger: "refind" }]);
            /**
             * CASES
             *   - the bookmarks were laid and read back -> the memory is primed from that read
             *   - nothing was laid, or the read misses  -> the attempt stands as recorded
             */
            const relocatedRead = bookmarksLaid ? await session.bookmarks.readRange(this.bookmarkNames) : null;
            if (relocatedRead !== null) this.finish({ read: relocatedRead, bookmarkRepairCount: 1, startedAtMs });
            return this.lastAttempt();
        });
    }

    /**
     * Full reconcile under the write lock: repair edges, read, recover when
     * lost, apply pending edge moves, and read again when anything was
     * written so the proposal sees the bookmarks where they now are.
     *
     * CASES, for `lostRecovery`
     *   - absent             -> the tracker finds out for itself whether its bookmarks are lost, and recovers them alone
     *   - what the batch did -> the batch found these bookmarks lost and recovered them with the others
     *                           (`reconcileBatch`): the tracker finishes over what that recovery laid
     */
    private async reconcileIn(session: WordSession, trigger: RecoveryTrigger, lostRecovery: LostRecoveryOutcome | null = null): Promise<AnchoredRead | null> {
        const logRead = (read: BookmarkedRangeRead): void => {
            const texts = new OoxmlRange(read.rangeOoxml).allChangesAcceptedTexts().filter((text) => text.trim().length > 0);
            const lastTextIndex = texts.length - 1;
            const lastAcceptedText = texts[lastTextIndex] ?? "";
            AnchorLog.record({
                kind: "read",
                bookmarkNames: this.bookmarkNames,
                paragraphCount: read.uniqueLocalIds.length,
                uniqueLocalIds: read.uniqueLocalIds,
                /**
                 * EXAMPLE
                 *   texts              = ["Intro", "Body"] -> texts[0] is "Intro"
                 *   texts              = []                  -> texts[0] ?? "" is ""
                 *
                 * WHY
                 *   `texts[0]` is the first non-blank accepted-text paragraph.
                 */
                firstText: AnchorLog.excerptStart(texts[0] ?? "", AnchorLog.EXCERPT_LENGTH),
                /**
                 * EXAMPLE
                 *   texts              = ["One", "Two", "Three"]
                 *   texts.length       = 3
                 *   texts.length - 1   = 2
                 *   texts[2]           = "Three"
                 *
                 * WHY
                 *   `texts.length - 1` lands on the last non-blank accepted-text paragraph.
                 */
                lastText: AnchorLog.excerptEnd(lastAcceptedText, AnchorLog.EXCERPT_LENGTH),
                previousText: read.previousParagraph ? AnchorLog.excerptStart(read.previousParagraph.text, AnchorLog.EXCERPT_LENGTH) : null,
                nextText: read.nextParagraph ? AnchorLog.excerptStart(read.nextParagraph.text, AnchorLog.EXCERPT_LENGTH) : null,
            });
        };
        const startedAtMs = performance.now();
        let bookmarkWritesMade = 0;
        let writesSinceTheLastRead = 0;
        const noteBookmarkWrite = (): void => {
            bookmarkWritesMade++;
            writesSinceTheLastRead++;
        };
        let read: BookmarkedRangeRead | null = null;
        /**
         * WHEN the batch has not already found these bookmarks lost
         *   CONSEQUENCE
         *     Repair the edges and read the range here; a lost range is found by this read.
         *
         * WHY
         *   Word can leave the bookmarks present but stretched onto neighbouring content, and an
         *   initial edge repair that writes a change is a write the read right after it already sees.
         */
        if (lostRecovery === null) {
            if (await timedBulkApplySpan("tracker_edges_repaired", () => session.bookmarks.repairEdges(this.bookmarkNames))) noteBookmarkWrite();
            read = await timedBulkApplySpan("tracker_range_read", () => session.bookmarks.readRange(this.bookmarkNames));
            writesSinceTheLastRead = 0;
        }
        /**
         * WHEN the bookmarks are lost: the batch found them so, or the write-lock read finds no bookmark range
         *   CONSEQUENCE
         *     Enter loss recovery: the batch's own recovery, or this tracker's alone.
         */
        if (read === null) {
            const recovery = lostRecovery ?? (await this.recoverLostOnItsOwn(session, trigger));
            /**
             * WHEN recovery did not place replacement bookmarks
             *   CONSEQUENCE
             *     Return null.
             *
             * NULL
             *   Recovery failed to produce a readable replacement range, or none was due for this trigger yet.
             */
            if (!recovery.bookmarksLaid) return null;
            noteBookmarkWrite();
            read = await timedBulkApplySpan("tracker_range_read", () => session.bookmarks.readRange(this.bookmarkNames));
            writesSinceTheLastRead = 0;
            /**
             * WHEN the post-recovery read still finds no bookmark range
             *   CONSEQUENCE
             *     Return null.
             *
             * NULL
             *   Recovery reported success, but there is still no stable range to hand back.
             */
            if (read === null) return null;
        }
        const pending = this.pendingMoves(read);
        const shouldCountEdgeRepairWrite = async (): Promise<boolean> => {
            const edgeRepairIsNeeded = pending.edgesNeedRepair;
            if (!edgeRepairIsNeeded) return false;
            return await session.bookmarks.repairEdges(this.bookmarkNames);
        };
        /**
         * WHEN the parsed read still shows invalid bookmark edges
         *   CONSEQUENCE
         *     Repair again, and count the write only if a repair changed Word.
         */
        if (await shouldCountEdgeRepairWrite()) noteBookmarkWrite();
        /**
         * WHEN trim analysis names word boundaries for the bookmark edges
         *   CONSEQUENCE
         *     Move the edges to those words, and count the write if Word changed.
         */
        if (await timedBulkApplySpan("tracker_edges_moved", () => session.bookmarks.moveEdgesToWords(this.bookmarkNames, pending.edgeMoves))) noteBookmarkWrite();
        /**
         * WHEN a bookmark write happened after the latest read
         *   CONSEQUENCE
         *     Re-read so `finish` sees the moved range. A write made before the latest read, such as
         *     the recovery's own lay, is already in it: reading again would return the same range.
         *
         * FALLBACK
         *   If the re-read fails, keep the last successful `read`.
         *
         * EXAMPLE
         *   recovery lays the bookmark, read, no edge written -> writesSinceTheLastRead = 0 -> no second read
         *   recovery lays the bookmark, read, an edge moved   -> writesSinceTheLastRead = 1 -> read again
         */
        if (writesSinceTheLastRead > 0) read = (await timedBulkApplySpan("tracker_range_read", () => session.bookmarks.readRange(this.bookmarkNames))) ?? read;
        logRead(read);
        return timedBulkApplySpanSync("tracker_finished", () => this.finish({ read, bookmarkRepairCount: bookmarkWritesMade, startedAtMs }));
    }

    /**
     * The trigger a lost tracker recovers under, or null when no recovery is due for it.
     *
     * WHEN the bookmarks are lost under the write lock
     *   CASES
     *     - first loss seen                 -> try recovery immediately
     *     - loss seen before, retry due     -> try recovery
     *     - loss seen before, retry not due -> null
     *
     * NULL
     *   Bookmarks are still lost, and this trigger has not earned another attempt yet.
     *
     * CASES, for the trigger it recovers under
     *   - every card on these bookmarks only deletes its paragraphs or leaves their text as it is
     *     (a comment) (`redlineWritesNothingWhereItsParagraphsAreGone`) -> "loss", whatever the trigger
     *   - a user's Apply or Refind, first loss or not                    -> the caller's trigger
     *   - first observed disappearance otherwise                         -> "loss"
     *   - later retry                                                    -> the caller's trigger
     *
     * WHY an Apply keeps its trigger on the first loss
     *   "loss" runs the local steps alone; the server step runs only for a user's Apply
     *   or Refind. An Apply that was the first to see the bookmark gone recovered under
     *   "loss", never asked the server, and failed the card with "We cannot find where
     *   to apply this"; the second click, a retry under "apply", asked it and landed.
     *
     * WHY a card that writes nothing back recovers by the local steps alone
     *   The paragraph id and the card's own anchors find the paragraph when it is still there
     *   (retyped with Track Changes off, its bookmark dropped), and the deletion is written there.
     *   The server and model steps never run for it: a deletion placed by a guess deletes someone
     *   else's words, as step 4 of the #15 live run deleted the paragraph that quoted 7.2
     *   (2026-10-18). When the local steps find nothing, the card is done
     *   (`ProposalApplier.applyOnBookmarks`, `cardIsDoneWhereItsParagraphsAreGone`).
     */
    private lossRecoveryTrigger(trigger: RecoveryTrigger): RecoveryTrigger | null {
        const firstLoss = this.memory.markLost();
        const bookmarksWereJustMarkedLost = firstLoss;
        const recoveryIsDueForTrigger = this.memory.recoveryDue(trigger);
        const shouldAttemptRecoveryNow = bookmarksWereJustMarkedLost || recoveryIsDueForTrigger;
        if (!shouldAttemptRecoveryNow) return null;
        const noCardWritesWhereItsParagraphsAreGone = !this.serverMayPlaceItsCard;
        if (noCardWritesWhereItsParagraphsAreGone) return "loss";
        return firstLoss && !TRIGGERS_A_USER_GAVE.has(trigger) ? "loss" : trigger;
    }

    /**
     * Recovers this tracker's lost bookmarks alone, for a caller that holds the write lock and
     * has not collected other lost trackers (`reconcileWithin`, or a bookmark that went missing
     * between the batch's question and this tracker's read): the same recovery as the batch's,
     * over a batch of one.
     */
    private async recoverLostOnItsOwn(session: WordSession, trigger: RecoveryTrigger): Promise<LostRecoveryOutcome> {
        const lossTrigger = this.lossRecoveryTrigger(trigger);
        /** WHEN no recovery is due for this trigger CONSEQUENCE nothing is read or written: the bookmarks stay lost. */
        if (lossTrigger === null) return NO_BOOKMARKS_LAID;
        const [recoveryOutcome] = await AnchorTracker.recoverLostTogether(session, this.deps.word.events, [{ tracker: this, trigger: lossTrigger }]);
        return recoveryOutcome;
    }

    /**
     * Runs the chain over a body already read, and writes nothing: what the chain found, for the
     * caller to lay and then record (`recordRecovery`).
     */
    private findPlacement(body: RecoveryBody, trigger: RecoveryTrigger): Promise<RecoveryOutcome> {
        /**
         * CASES
         *   - a card on these bookmarks writes words back where they are gone -> its words
         *   - none does                                                       -> the first card's
         *   - no card is attached                                             -> ""
         */
        const redlineDesiredText = (this.redlineWrittenWhereItsParagraphsAreGone ?? this.redline)?.source.allChangesAcceptedTexts.join("\n") ?? "";
        const recoveryContext: RecoveryContext = {
            body,
            memory: this.memory,
            bookmarkNames: this.bookmarkNames,
            redlineDesiredText,
            cardWritesWhereItsParagraphsAreGone: this.redlineWrittenWhereItsParagraphsAreGone !== null,
            trigger,
        };
        return this.chain.recover(recoveryContext);
    }

    /**
     * Records the attempt a chain's finding made, whatever the outcome, once the caller has tried
     * to lay the bookmarks on it.
     *
     * CASES
     *   - outcome is "recovered" and the bookmarks were laid -> the placement is the chain's
     *   - outcome is "recovered" and nothing was laid        -> recorded as not found; the placement stays
     *   - any other outcome                                   -> record the attempt only
     *
     * WHY
     *   A relocation the server found was reported recovered with no bookmark laid
     *   when the paragraph ids it named were gone, so the next Go to click found
     *   nothing and asked the server again (T20, 2026-10-16).
     */
    private recordRecovery(trigger: RecoveryTrigger, { step, result }: RecoveryOutcome, bookmarksLaid: boolean): void {
        const tier: RecoveryTier | null = step?.tier ?? null;
        if (bookmarksLaid && result.outcome === "recovered") this.placementValue = result.placement;
        const paragraphsFoundAreGone = result.outcome === "recovered" && !bookmarksLaid;
        const attempt: RecoveryAttempt = {
            attemptedAtMs: Date.now(),
            trigger,
            tier,
            outcome: paragraphsFoundAreGone ? "not_found" : result.outcome,
            explanation: paragraphsFoundAreGone ? PARAGRAPHS_FOUND_ARE_GONE_EXPLANATION : result.explanation,
            placement: this.placementValue,
            /** The places a conflicted answer named; every other outcome names none. */
            candidates: result.outcome === "ambiguous" ? result.candidates : [],
            checks: result.checks,
        };
        this.memory.record(attempt);
        AnchorLog.record({ kind: "recovery", bookmarkNames: this.bookmarkNames, attempt });
    }

    /** Parses the read once and asks it both bookmark questions. */
    private pendingMoves(read: BookmarkedRangeRead): PendingBookmarkMoves {
        const range = new OoxmlRange(read.rangeOoxml);
        const trims = range.bookmarkSpans({ bookmarkNames: this.bookmarkNames, neighbourTexts: this.memory.neighbourTexts() });
        return {
            edgesNeedRepair: range.bookmarkEdgesNeedMove(this.bookmarkNames),
            edgeMoves: {
                /**
                 * WHY
                 *   The start edge comes from the first trimmed paragraph span.
                 *
                 * EXAMPLE
                 *   trims has spans for paragraphs 0 and 1
                 *   trims[0]?.startAfterWord -> start-edge move
                 *   trims[1]?.startAfterWord -> not used for the bookmark start
                 */
                startAfterWord: trims[0]?.startAfterWord ?? null,
                /**
                 * WHY
                 *   The end edge comes from the last trimmed paragraph span.
                 *
                 * EXAMPLE
                 *   trims.length            = 2
                 *   trims.length - 1        = 1
                 *   trims[1]?.endBeforeWord -> end-edge move
                 */
                endBeforeWord: trims[trims.length - 1]?.endBeforeWord ?? null,
            },
        };
    }

    /**
     * Turns a raw read into an `AnchoredRead`, remembering what the next
     * recovery will need, with the part of each paragraph the card covers
     * (`coveredSpansOver`).
     */
    private finish({ read, bookmarkRepairCount, startedAtMs }: { read: BookmarkedRangeRead; bookmarkRepairCount: number; startedAtMs: number }): AnchoredRead {
        this.memory.remember(read);
        const allChangesAcceptedTexts = new OoxmlRange(read.rangeOoxml).allChangesAcceptedTexts();
        const coveredSpans = this.coveredSpansOver(read.rangeOoxml);
        this.memory.anchored({
            read,
            paragraphTexts: allChangesAcceptedTexts,
            /**
             * EXAMPLE
             *   index                  = 1
             *   allChangesAcceptedTexts[1]       = second paragraph text
             *   span.startOffset       = 5
             *   span.endOffset         = 9
             *   slice(5, 9)            -> characters 5 through 8
             *
             * WHY
             *   Each span is sliced from the paragraph text at the same index.
             */
            spanText: coveredSpans.map((span, index) => (allChangesAcceptedTexts[index] ?? "").slice(span.startOffset, span.endOffset)).join(" "),
        });
        return {
            rangeOoxml: read.rangeOoxml,
            wordsExportAsGiven: read.wordsExportAsGiven,
            paragraphIds: read.paragraphIds,
            uniqueLocalIds: read.uniqueLocalIds,
            coveredSpans,
            bookmarkRepairCount,
            elapsedMs: performance.now() - startedAtMs,
        };
    }

    /**
     * The part of each paragraph of a read that this tracker's card covers, one span per
     * paragraph: the bookmarks' extent, shrunk off a neighbour's words the bookmark
     * swallowed (`OoxmlRange.bookmarkSpans`, with the neighbours this tracker remembers).
     * For an inside-paragraph placement the first span is narrowed to the phrase the
     * server named, sized to the redline's original text, so the engine edits only the
     * sentence the redline is about and not the whole paragraph it was merged into.
     *
     * WHY one reading for Apply and Apply All
     *   Apply builds the card over these spans (`ProposalApplier`), and Apply All reads each
     *   card over the same spans of its page (`LocalBulkApplyInput.coveredSpansOver`). Read
     *   whole, a paragraph the user merged with its neighbour handed the neighbour's words to
     *   Apply All's conflict choice as "Already in the document", and keeping the card's words
     *   struck the neighbour's sentence (PC13, 2026-10-19).
     *
     * EXAMPLE
     *   "Notices go to the addresses above. Customer shall pay within thirty days of invoice."
     *   with the card's bookmark on the second sentence -> [{ startOffset: 35, endOffset: 84 }]
     *
     * RETURNS
     *   Nothing is remembered: the read is not the tracker's own (`finish` remembers that).
     */
    coveredSpansOver(rangeOoxml: string): AcceptedTextSpan[] {
        const insideSpan = (paragraphText: string, anchorPhrase: string, originalLength: number): AcceptedTextSpan | null => {
            const phraseOffset = (): number | null => {
                const words = (anchorPhrase.match(/\S+/g) ?? []).map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
                /**
                 * WHEN the anchor phrase has no non-whitespace words
                 *   CONSEQUENCE
                 *     Return null: there is no inside-paragraph phrase to search for.
                 *
                 * NULL
                 *   No match attempt is possible for this paragraph.
                 */
                if (words.length === 0) return null;
                const match = new RegExp(words.join("\\s+"), "i").exec(paragraphText);
                /**
                 * CASES
                 *   - phrase matches in order -> return the starting character offset
                 *   - no match                -> return null
                 *
                 * NULL
                 *   The merged paragraph text cannot be narrowed by this phrase.
                 */
                return match ? match.index : null;
            };
            const startOffset = phraseOffset();
            /**
             * WHEN the anchor phrase is not found in the merged paragraph text
             *   CONSEQUENCE
             *     Return null instead of narrowing the span.
             *
             * NULL
             *   Keep the broader bookmark-derived span.
             */
            if (startOffset === null) return null;
            /**
             * CASES
             *   - originalLength > 0 -> end at `startOffset + originalLength`, capped at paragraph end
             *   - originalLength = 0 -> end at paragraph end
             *
             * EXAMPLE
             *   startOffset          = 12
             *   originalLength       = 20
             *   paragraphText.length = 25
             *   12 + 20              = 32
             *   endOffset            = min(25, 32) = 25
             */
            return { startOffset, endOffset: originalLength > 0 ? Math.min(paragraphText.length, startOffset + originalLength) : paragraphText.length };
        };

        const range = new OoxmlRange(rangeOoxml);
        const trims = range.bookmarkSpans({ bookmarkNames: this.bookmarkNames, neighbourTexts: this.memory.neighbourTexts() });
        const allChangesAcceptedTexts = range.allChangesAcceptedTexts();
        const coveredSpans = trims.map((trim) => trim.span);
        /**
         * WHEN recovery placement is `inside_paragraph` and there is a covered span
         *   CONSEQUENCE
         *     Try to narrow the first covered paragraph to the anchor phrase.
         */
        if (this.placementValue.kind === "inside_paragraph" && coveredSpans.length > 0) {
            const firstOriginalParagraphLength = this.redline?.source.textsBeforeAuthor[0]?.length ?? 0;
            /**
             * WHY
             *   Inside-paragraph recovery narrows only the first covered paragraph.
             *
             * EXAMPLE
             *   allChangesAcceptedTexts[0]                    -> merged paragraph text to search
             *   textsBeforeAuthor[0]?.length        -> original covered length for that paragraph
             */
            const narrowed = insideSpan(allChangesAcceptedTexts[0] ?? "", this.placementValue.anchorPhrase, firstOriginalParagraphLength);
            /**
             * WHY
             *   Replace entry 0 because the narrowed span belongs to the first covered paragraph.
             *
             * WHEN `narrowed` is null
             *   CONSEQUENCE
             *     Leave `coveredSpans[0]` unchanged.
             */
            if (narrowed) coveredSpans[0] = narrowed;
        }
        return coveredSpans;
    }
}
